import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Nav, Row, Spinner } from 'react-bootstrap-5';
import ConnectTagManager from '../tags/connect_tag_manager';
import TagHeader from '../tags/tag_header';
import { fetchContact, updateContactRequest } from '../../helpers/prospect_api';
import NotesCard from '../call_list/notes';
import ContactInformation from '../call_list/contact_information';
import OwnedPropertiesCard from './owned_properties_card';
import ActivityHistoryCard from './activity_history_card';
import TasksCard from './tasks_card';
import { Provider } from 'react-redux';
import { SET_ACTIVITIES } from 'bundles/connect/components/store/actions/activities';
import configureStore from 'bundles/connect/components/store/configure';
import ActionsDropdown from './action_dropdown';
import { CONTACT_MODEL } from '../../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
  faCircleCheck,
  faHouseBuilding,
  faNotes,
  faRectangleVerticalHistory
} from '@fortawesome/pro-solid-svg-icons';
import type { Contact, Profile } from 'bundles/connect/types';
import OffMarketPropertyOffcanvas from '../search/components/off_market_property_offcanvas';

const TAB_PROPERTIES = 'properties';
const TAB_TASKS = 'tasks';
const TAB_ACTIVITY_HISTORY = 'activity_history';
const TAB_NOTES = 'notes';

type ContactDetailsProps = {
  id: number;
  profile: Profile;
  onDelete: (id: number, contactType: string) => void;
  onUpdate: (contact: Contact) => void;
}

export default function ContactDetails({ id, profile, onDelete, onUpdate }: ContactDetailsProps) {
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(TAB_TASKS);
  const [showTagsHeader, setShowTagsHeader] = useState(false);
  const store = useMemo(() => configureStore({ ui: { profile }, activities: [] }), []);

  const initContact = useCallback((performUpdate = true) => {
    if (!id) return;

    fetchContact(id)
      .then((res) => {
        setContact(res);
        store.dispatch({ type: SET_ACTIVITIES, payload: res.activities });
        if (performUpdate) onUpdate(res);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    initContact(false);
  }, [id]);

  const handleUpdate = (id: number, contact: Contact) => {
    return updateContactRequest(id, contact).then(() => {
      initContact();
    });
  };

  if (loading || !contact) {
    return (
      <Card.Body className="text-center">
        <Spinner animation="border" variant="secondary" />
      </Card.Body>
    );
  }

  return (
    <Provider store={store}>
      <>
        <Card.Body className="d-flex flex-column gap-4">
          <Row className="align-items-center">
            <Col>
              <h4 className="m-0 privacy-mask">{contact.name}</h4>
              {(contact.company || contact.jobTitle) && (
                <div className="text-muted">
                  {contact?.company?.name}
                  {contact.jobTitle && (`${contact.company ? ' •' : ''} ${contact.jobTitle}`)}
                </div>
              )}
            </Col>
            <Col xs="auto">
              <ActionsDropdown
                contact={contact}
                onDelete={() => onDelete(contact.id, contact.contactType)}
                onUpdate={handleUpdate}
              />
            </Col>
          </Row>
          <ContactInformation contact={contact} />
          {showTagsHeader && (<TagHeader />)}
          <ConnectTagManager
            buttonProperties={{
              buttonClasses: 'p-0',
              iconRight: <FontAwesomeIcon icon={faPlus} />,
              variant: 'text',
            }}
            initialTags={contact.tags}
            recordId={contact.id}
            recordType={CONTACT_MODEL}
            setShowTagsHeader={setShowTagsHeader}
          />
        </Card.Body>
        <Nav activeKey={tab} variant="tabs" onSelect={(e: string) => setTab(e)}>
          <Nav.Item>
            <Nav.Link className="border-start-0 py-2" eventKey={TAB_TASKS}>
              <FontAwesomeIcon icon={faCircleCheck} />
              Tasks
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link className="py-2" eventKey={TAB_ACTIVITY_HISTORY}>
              <FontAwesomeIcon icon={faRectangleVerticalHistory} />
              Activity History
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link className="py-2" eventKey={TAB_PROPERTIES}>
              <FontAwesomeIcon icon={faHouseBuilding} />
              Portfolio
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link className="py-2" eventKey={TAB_NOTES}>
              <FontAwesomeIcon icon={faNotes} />
              Notes
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Card.Body>
          {tab === TAB_TASKS && <TasksCard contact={contact} />}
          {tab === TAB_ACTIVITY_HISTORY && <ActivityHistoryCard />}
          {tab === TAB_PROPERTIES && <OwnedPropertiesCard contactId={id} />}
          {tab === TAB_NOTES && <NotesCard recordId={id} recordType={CONTACT_MODEL} />}
        </Card.Body>
        <OffMarketPropertyOffcanvas fromConsole={false} />
      </>
    </Provider>
  );
}
